import * as React from 'react';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { InlineAlert } from './inline-alert';
import { ModalOverlay, modalSheetClassName } from './modal-overlay';
import { dialogTitleClassName } from './typography';
import { useModalDialog } from './use-modal-dialog';

export interface ConfirmDialogProps {
    open: boolean;
    title: string;
    description?: React.ReactNode;
    children?: React.ReactNode;
    confirmLabel: string;
    pendingLabel?: string;
    destructive?: boolean;
    isPending?: boolean;
    error?: string | null;
    returnFocusRef?: React.RefObject<HTMLElement>;
    onConfirm: () => unknown;
    onCancel: () => void;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
    return (
        typeof value === 'object' &&
        value !== null &&
        'then' in value &&
        typeof value.then === 'function'
    );
}

export function ConfirmDialog({
    open,
    title,
    description,
    children,
    confirmLabel,
    pendingLabel,
    destructive = false,
    isPending = false,
    error,
    returnFocusRef,
    onConfirm,
    onCancel,
}: ConfirmDialogProps) {
    const cancelRef = React.useRef<HTMLButtonElement>(null);
    const [internalPending, setInternalPending] = React.useState(false);
    const mountedRef = React.useRef(true);
    const pending = Boolean(isPending) || internalPending;

    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    React.useEffect(() => {
        if (!open) {
            setInternalPending(false);
        }
    }, [open]);

    const handleCancel = React.useCallback(() => {
        if (!pending) {
            onCancel();
        }
    }, [onCancel, pending]);

    const { titleId, descriptionId, panelProps } = useModalDialog({
        open,
        onClose: handleCancel,
        role: 'alertdialog',
        initialFocusRef: cancelRef,
        returnFocusRef,
    });

    const handleConfirm = () => {
        if (pending) {
            return;
        }
        const result = onConfirm();
        if (isPromiseLike(result)) {
            setInternalPending(true);
            void Promise.resolve(result).then(
                () => {
                    if (mountedRef.current) {
                        setInternalPending(false);
                    }
                },
                () => {
                    if (mountedRef.current) {
                        setInternalPending(false);
                    }
                },
            );
        }
    };

    if (!open) {
        return null;
    }

    return (
        <ModalOverlay>
            <div
                {...panelProps}
                aria-describedby={description ? descriptionId : undefined}
                className={cn(modalSheetClassName, 'w-full max-w-md p-5')}
            >
                <h2 id={titleId} className={dialogTitleClassName}>
                    {title}
                </h2>
                {description ? (
                    typeof description === 'string' ? (
                        <p id={descriptionId} className="mt-2 text-sm text-neutral-600">
                            {description}
                        </p>
                    ) : (
                        <div id={descriptionId} className="mt-2 text-sm text-neutral-600">
                            {description}
                        </div>
                    )
                ) : null}
                {children ? <div className="mt-3 text-sm text-neutral-600">{children}</div> : null}
                {error ? (
                    <InlineAlert tone="error" className="mt-4">
                        {error}
                    </InlineAlert>
                ) : null}
                <div className="mt-6 flex flex-wrap justify-end gap-2">
                    <Button
                        ref={cancelRef}
                        type="button"
                        variant="outline"
                        disabled={pending}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant={destructive ? 'destructive' : 'default'}
                        disabled={pending}
                        loading={pending}
                        onClick={handleConfirm}
                    >
                        {pending ? (pendingLabel ?? confirmLabel) : confirmLabel}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
}
